/**
 * Imperative board UI alerts (replaces window.alert / window.confirm on canvas).
 * BoardAlertHost must be mounted for these to resolve with a UI dialog.
 */

let hostHandler = null;
let requestSeq = 0;

/**
 * @param {(request: object) => Promise<boolean>} handler
 * @returns {() => void} unregister
 */
export function registerBoardAlertHost(handler) {
  hostHandler = handler;
  return () => {
    if (hostHandler === handler) hostHandler = null;
  };
}

function nextRequest(partial) {
  requestSeq += 1;
  return { id: requestSeq, ...partial };
}

/**
 * Show a confirm dialog. Resolves true if confirmed, false if cancelled.
 *
 * If `onConfirm` is provided it is called after the user clicks the confirm
 * button. The dialog stays open (showing a loading spinner) until the callback
 * resolves, then closes automatically.
 *
 * @param {{
 *   title?: string,
 *   message: string,
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   danger?: boolean,
 *   href?: string,
 *   hrefLabel?: string,
 *   onConfirm?: () => Promise<void>,
 * }} options
 * @returns {Promise<boolean>}
 */
export function boardConfirm(options = {}) {
  const message = String(options.message || '').trim();
  if (!message) return Promise.resolve(false);

  const request = nextRequest({
    mode: 'confirm',
    title: options.title || 'Confirm',
    message,
    confirmLabel: options.confirmLabel || 'OK',
    cancelLabel: options.cancelLabel || 'Cancel',
    danger: Boolean(options.danger),
    href: options.href || null,
    hrefLabel: options.hrefLabel || null,
    onConfirm: typeof options.onConfirm === 'function' ? options.onConfirm : null,
  });

  if (!hostHandler) {
    console.warn('BoardAlertHost is not mounted; confirm cancelled.', request.message);
    return Promise.resolve(false);
  }

  return hostHandler(request);
}

/**
 * Show an alert dialog. Resolves when dismissed.
 * @param {{ title?: string, message: string, confirmLabel?: string, href?: string, hrefLabel?: string }} options
 * @returns {Promise<void>}
 */
export async function boardAlert(options = {}) {
  const message = String(options.message || '').trim();
  if (!message) return;

  const request = nextRequest({
    mode: 'alert',
    title: options.title || 'Notice',
    message,
    confirmLabel: options.confirmLabel || 'OK',
    cancelLabel: null,
    danger: false,
    href: options.href || null,
    hrefLabel: options.hrefLabel || null,
  });

  if (!hostHandler) {
    console.warn('BoardAlertHost is not mounted; alert skipped.', request.message);
    return;
  }

  await hostHandler(request);
}

/**
 * Show a small prompt dialog with a text field.
 * Resolves to the trimmed string on confirm, or null if cancelled.
 * @param {{
 *   title?: string,
 *   message?: string,
 *   label?: string,
 *   placeholder?: string,
 *   defaultValue?: string,
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   required?: boolean,
 * }} options
 * @returns {Promise<string|null>}
 */
export function boardPrompt(options = {}) {
  const request = nextRequest({
    mode: 'prompt',
    title: options.title || 'Enter a value',
    message: String(options.message || '').trim(),
    label: options.label || null,
    placeholder: options.placeholder || '',
    defaultValue: options.defaultValue != null ? String(options.defaultValue) : '',
    confirmLabel: options.confirmLabel || 'OK',
    cancelLabel: options.cancelLabel || 'Cancel',
    required: Boolean(options.required),
    danger: false,
    href: null,
    hrefLabel: null,
  });

  if (!hostHandler) {
    console.warn('BoardAlertHost is not mounted; prompt cancelled.', request.title);
    return Promise.resolve(null);
  }

  return hostHandler(request);
}
